import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import TablePagination from '@mui/material/TablePagination';
import Alert from '@mui/material/Alert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BusinessIcon from '@mui/icons-material/Business';
import CloseIcon from '@mui/icons-material/Close';

import { sbcParcelApi } from '../../api/sbcparcel.api';
import { ProcessedParcel, FilterMode, SortMode, ToggleTarget, WeekBucket } from './types';
import {
  buildSearchHaystack,
  computeProgress,
  formatRelativeTime,
  formatWeekTitle,
  parseWeekString,
  weekBucket,
  WEEK_BUCKET_LABELS,
} from './utils';
import { SummaryStats } from './SummaryStats';
import { PipelineToolbar } from './PipelineToolbar';
import { WeekCard } from './WeekCard';
import { ConfirmStatusDialog } from './ConfirmStatusDialog';
import { RerunDialog } from './RerunDialog';
import { RerunTarget, requireField, sbcParcelApi as api } from '../../api/sbcparcel.api';
import { useCustomer } from '../../customer';

const describeUploadedFiles = (row: ProcessedParcel | null): string | null => {
  if (!row) return null;
  const counts = [
    ['UPS', row.UPSImportFileCount],
    ['GLS', row.GLSImportFileCount],
    ['FBO', row.FBOImportFileCount],
    ['DHL', row.DHLImportFileCount],
  ] as const;

  // No count at all means no upload has been recorded for this week — not that the week is
  // empty. Claiming "no files uploaded" there is a guess, and in a confirmation dialog it is
  // the guess that reads as fact, so say nothing instead.
  if (counts.every(([, count]) => count === null || count === undefined)) return null;

  const parts = counts
    .filter(([, count]) => Boolean(count))
    .map(([label, count]) => `${count} ${label} file${count === 1 ? '' : 's'}`);
  return parts.length > 0 ? parts.join(', ') : 'no files uploaded';
};

export const ParcelInvoiceManagement: React.FC = () => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [rows, setRows] = React.useState<ProcessedParcel[]>([]);
  const [openRows, setOpenRows] = React.useState<{ [key: string]: boolean }>({});
  const [confirmState, setConfirmState] = React.useState<ToggleTarget | null>(null);

  // Which week a rerun would process, straight from the function. Null when this customer has
  // no manual run — Specialized Bike and InExpress have their own processors.
  const [rerunTarget, setRerunTarget] = React.useState<RerunTarget | null>(null);
  const [rerunUnavailable, setRerunUnavailable] = React.useState(false);
  const [rerunOpen, setRerunOpen] = React.useState(false);
  const [rerunning, setRerunning] = React.useState(false);

  const [search, setSearch] = React.useState('');
  const [filter, setFilter] = React.useState<FilterMode>('all');
  const [sort, setSort] = React.useState<SortMode>('newest');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const [includeOlder, setIncludeOlder] = React.useState(false);

  const { customer } = useCustomer();

  const [welcomeOpen, setWelcomeOpen] = React.useState(true);
  React.useEffect(() => {
    setWelcomeOpen(true);
    const t = setTimeout(() => setWelcomeOpen(false), 4000);
    return () => clearTimeout(t);
  }, [customer.id]);

  const [lastFetched, setLastFetched] = React.useState<number | null>(null);
  const [clockTick, setClockTick] = React.useState<number>(Date.now());
  React.useEffect(() => {
    const t = setInterval(() => setClockTick(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await sbcParcelApi.getProcessedParcels(customer.id);
      setRows(Array.isArray(data) ? data : []);
      setLastFetched(Date.now());
    } catch (e) {
      setRows([]);
      setError('Unable to load weekly invoice data. Please retry.');
    } finally {
      setLoading(false);
    }
  }, [customer.id]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  React.useEffect(() => {
    let cancelled = false;
    // Cleared before asking, not after answering: otherwise the previous customer's week sits
    // beside the new customer's name until the response lands, on the one screen whose whole
    // job is to state exactly what is about to happen.
    setRerunTarget(null);
    setRerunUnavailable(false);
    setRerunOpen(false);
    api
      .getRerunTarget(customer.id)
      .then((result: any) => {
        if (cancelled) return;
        if (result && typeof result.week === 'string') {
          setRerunTarget(result as RerunTarget);
          setRerunUnavailable(false);
          return;
        }
        // Only the function saying so means this customer has no manual run — Specialized Bike
        // and InExpress use their own processors. A bare 404 proves nothing: the portal's own API
        // returns one for an unrecognised path too, so anything without the flag is "could not
        // ask", which has to look different on screen.
        setRerunTarget(null);
        setRerunUnavailable(result?.data?.supported !== false);
      })
      .catch(() => {
        if (cancelled) return;
        setRerunTarget(null);
        setRerunUnavailable(true);
      });
    return () => {
      cancelled = true;
    };
  }, [customer.id]);

  const handleConfirmRerun = React.useCallback(async () => {
    setRerunning(true);
    setError(null);
    try {
      requireField<RerunTarget>(await api.triggerRerun(customer.id), 'week');
      setRerunOpen(false);
      await fetchData();
    } catch (e: any) {
      setRerunOpen(false);
      setError(e?.message || 'The rerun did not complete. Check the week before running it again.');
    } finally {
      setRerunning(false);
    }
  }, [customer.id, fetchData]);

  const isRecentBucket = (row: ProcessedParcel) => {
    const b = weekBucket(row.Week);
    return b === 'upcoming' || b === 'last30';
  };

  const matchesSearchAndFilter = React.useCallback(
    (row: ProcessedParcel) => {
      const q = search.trim().toLowerCase();
      if (q && !buildSearchHaystack(row.Week).includes(q)) return false;
      if (filter !== 'all') {
        const p = computeProgress(row, customer.stages);
        if (filter === 'complete' && !p.isComplete) return false;
        if (filter === 'pending' && p.isComplete) return false;
        if (filter === 'remits' && !(p.remitsTotal > 0 && p.remitsUploaded < p.remitsTotal)) return false;
      }
      return true;
    },
    [search, filter, customer.stages]
  );

  const filteredRows = React.useMemo(() => {
    return rows.filter((row) => {
      if (!matchesSearchAndFilter(row)) return false;
      if (!includeOlder && !isRecentBucket(row)) return false;
      return true;
    });
  }, [rows, matchesSearchAndFilter, includeOlder]);

  const olderMatchCount = React.useMemo(() => {
    if (includeOlder) return 0;
    return rows.reduce((acc, row) => {
      if (matchesSearchAndFilter(row) && !isRecentBucket(row)) return acc + 1;
      return acc;
    }, 0);
  }, [rows, matchesSearchAndFilter, includeOlder]);

  const sortedRows = React.useMemo(() => {
    const out = filteredRows.slice();
    const dateOf = (r: ProcessedParcel) => parseWeekString(r.Week)?.getTime() ?? 0;
    out.sort((a, b) => {
      const da = dateOf(a);
      const db = dateOf(b);
      switch (sort) {
        case 'oldest':
          return da - db;
        case 'action': {
          const ca = computeProgress(a, customer.stages).isComplete ? 1 : 0;
          const cb = computeProgress(b, customer.stages).isComplete ? 1 : 0;
          if (ca !== cb) return ca - cb;
          return db - da;
        }
        case 'complete': {
          const ca = computeProgress(a, customer.stages).isComplete ? 0 : 1;
          const cb = computeProgress(b, customer.stages).isComplete ? 0 : 1;
          if (ca !== cb) return ca - cb;
          return db - da;
        }
        case 'newest':
        default:
          return db - da;
      }
    });
    return out;
  }, [filteredRows, sort, customer.stages]);

  const pagedRows = React.useMemo(
    () => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedRows, page, rowsPerPage]
  );

  // reset page when inputs change
  React.useEffect(() => {
    setPage(0);
  }, [search, filter, sort, rowsPerPage, includeOlder]);

  const showGroupHeaders = sort === 'newest' || sort === 'oldest';

  const describeConfirmTarget = (target: ToggleTarget | null): string => {
    if (!target) return '';
    const row = rows.find((r) => r.id === target.rowId);
    if (!row) return '';
    const weekTitle = formatWeekTitle(row.Week);

    const apMatch = target.field.match(/AccountsPayable\[(\d+)\]\.(\w+)/);
    if (apMatch) {
      const idx = parseInt(apMatch[1], 10);
      const item = row.AccountsPayable?.[idx];
      const acct = item?.EDIAccountNumber || `#${idx + 1}`;
      return `Remit for Account ${acct} · ${weekTitle}`;
    }
    if (target.field === 'InvoiceImportUploaded') {
      return `Invoice · ${weekTitle}`;
    }
    return weekTitle;
  };

  const handleToggleExpand = (id: string) =>
    setOpenRows((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleRequestToggleInvoice = (rowId: string, currentValue: boolean) => {
    setConfirmState({ rowId, field: 'InvoiceImportUploaded', currentValue });
  };

  const handleRequestToggleRemit = (
    rowId: string,
    apIndex: number,
    currentValue: boolean
  ) => {
    setConfirmState({
      rowId,
      field: `AccountsPayable[${apIndex}].RemittanceFileUploaded`,
      currentValue,
    });
  };

  const handleCancelConfirm = () => setConfirmState(null);

  const handleConfirmToggle = async () => {
    if (!confirmState) return;
    setLoading(true);
    try {
      const apMatch = confirmState.field.match(/AccountsPayable\[(\d+)\]\.(\w+)/);
      if (apMatch) {
        const idx = parseInt(apMatch[1], 10);
        const field = apMatch[2];
        const row = rows.find((r) => r.id === confirmState.rowId);
        const item = row?.AccountsPayable?.[idx];
        if (!row || !item) throw new Error('AccountsPayable item not found');

        const updatedItem = {
          ...item,
          [field]: !confirmState.currentValue,
          FedExACHNumber: item.FedExACHNumber,
          FedExSettlementDate: item.FedExSettlementDate,
          APRemitInformationProvided: true,
        };
        await sbcParcelApi.updateProcessedParcel(confirmState.rowId, {
          AccountsPayable: [updatedItem],
        });
      } else {
        await sbcParcelApi.updateProcessedParcel(confirmState.rowId, {
          [confirmState.field]: !confirmState.currentValue,
        });
      }
      setConfirmState(null);
      await fetchData();
    } catch (e) {
      setLoading(false);
      setError('Failed to update status. Please retry.');
      setConfirmState(null);
    }
  };

  const olderToggleButton = (
    <Button
      variant="outlined"
      onClick={() => setIncludeOlder((prev) => !prev)}
      startIcon={includeOlder ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      sx={{
        alignSelf: 'center',
        borderColor: 'grey.300',
        color: 'text.primary',
        backgroundColor: 'background.paper',
        '&:hover': {
          backgroundColor: 'grey.50',
          borderColor: 'grey.400',
        },
      }}
    >
      {includeOlder
        ? 'Hide older weeks'
        : olderMatchCount === 1
          ? 'Show 1 older week'
          : `Show ${olderMatchCount} older weeks`}
    </Button>
  );

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2.25 }}>
        Parcel Invoice Management
      </Typography>

      <Collapse in={welcomeOpen} timeout={250}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1.5,
              maxWidth: 520,
              px: 2.5,
              py: 1.25,
              borderRadius: 2,
              backgroundColor: 'primary.main',
              color: 'white',
              boxShadow: '0 6px 16px rgba(29, 78, 216, 0.25)',
            }}
          >
            <BusinessIcon sx={{ fontSize: 22, flexShrink: 0 }} />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 0.75,
                whiteSpace: 'nowrap',
                letterSpacing: '0.1px',
              }}
            >
              <Box component="span" sx={{ fontSize: 15, fontWeight: 500, lineHeight: 1 }}>
                Viewing as
              </Box>
              <Box component="span" sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>
                {customer.name}
              </Box>
            </Box>
            <IconButton
              onClick={() => setWelcomeOpen(false)}
              aria-label="Dismiss banner"
              size="small"
              sx={{
                color: 'white',
                p: 0.5,
                ml: 0.5,
                mr: -0.5,
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.15)' },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
      </Collapse>

      <SummaryStats rows={rows} filter={filter} onFilterChange={setFilter} />

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1.75, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <PipelineToolbar
        search={search}
        onSearchChange={setSearch}
        filter={filter}
        onFilterChange={setFilter}
        sort={sort}
        onSortChange={setSort}
        onRefresh={fetchData}
        rerunWeekDisplay={rerunTarget?.weekDisplay ?? null}
        rerunUnavailable={rerunUnavailable}
        rerunning={rerunning}
        onRerun={() => setRerunOpen(true)}
        loading={loading}
        lastRefreshLabel={formatRelativeTime(lastFetched, clockTick)}
        lastRefreshTooltip={lastFetched ? new Date(lastFetched).toLocaleString() : undefined}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {pagedRows.length === 0 && !loading ? (
          <Box
            sx={{
              backgroundColor: 'background.paper',
              border: '1px dashed',
              borderColor: 'grey.300',
              borderRadius: 2,
              py: 6,
              textAlign: 'center',
              color: 'text.secondary',
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
              No recent weeks match your filters
            </Typography>
            {olderMatchCount > 0 ? (
              <>
                <Typography variant="body2" sx={{ mt: 0.5, mb: 1.5 }}>
                  {olderMatchCount === 1
                    ? '1 older week matches.'
                    : `${olderMatchCount} older weeks match.`}{' '}
                  Older weeks are hidden by default.
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => setIncludeOlder(true)}
                  startIcon={<ExpandMoreIcon />}
                >
                  {olderMatchCount === 1
                    ? 'Show 1 older week'
                    : `Show ${olderMatchCount} older weeks`}
                </Button>
              </>
            ) : (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Try clearing the search or switching the filter to “All”.
              </Typography>
            )}
          </Box>
        ) : (
          (() => {
            let lastBucket: WeekBucket | null = null;
            return pagedRows.map((row) => {
              const bucket = weekBucket(row.Week);
              const showDivider = showGroupHeaders && bucket !== lastBucket;
              lastBucket = bucket;
              return (
                <React.Fragment key={row.id}>
                  {showDivider && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        mt: row !== pagedRows[0] ? 1.25 : 0,
                        px: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          color: 'text.secondary',
                          fontSize: 11,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.9px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {WEEK_BUCKET_LABELS[bucket]}
                      </Typography>
                      <Box sx={{ flex: 1, height: '1px', backgroundColor: 'divider' }} />
                    </Box>
                  )}
                  <WeekCard
                    row={row}
                    expanded={!!openRows[row.id]}
                    onToggleExpand={handleToggleExpand}
                    onRequestToggleInvoice={handleRequestToggleInvoice}
                    onRequestToggleRemit={handleRequestToggleRemit}
                  />
                </React.Fragment>
              );
            });
          })()
        )}
      </Box>

      {(olderMatchCount > 0 || includeOlder) && pagedRows.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.75 }}>
          {olderToggleButton}
        </Box>
      )}

      {sortedRows.length > rowsPerPage && (
        <Box
          sx={{
            mt: 1.75,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <TablePagination
            component="div"
            count={sortedRows.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            sx={{
              '.MuiTablePagination-toolbar': { minHeight: 52 },
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                fontWeight: 500,
                color: 'text.secondary',
              },
            }}
          />
        </Box>
      )}

      <ConfirmStatusDialog
        open={Boolean(confirmState)}
        currentValue={Boolean(confirmState?.currentValue)}
        customerName={customer.name}
        itemDescription={describeConfirmTarget(confirmState)}
        onConfirm={handleConfirmToggle}
        onCancel={handleCancelConfirm}
      />

      <RerunDialog
        open={rerunOpen}
        customerName={customer.name}
        weekDisplay={rerunTarget?.weekDisplay ?? ''}
        fileSummary={describeUploadedFiles(
          rows.find((r) => r.Week === rerunTarget?.week) ?? null
        )}
        running={rerunning}
        onCancel={() => setRerunOpen(false)}
        onConfirm={handleConfirmRerun}
      />

    </Box>
  );
};

export default ParcelInvoiceManagement;
